// Draw a version of the page with the real components, so the page works
// while it is edited. Whatever a merge leaves, it draws: an unknown
// component, a broken prop or a component that throws becomes a marked
// placeholder, never a blank page. The same code draws a published page,
// with no editor around it.
// meets: code/any-tree, code/live
import { Component as PComponent, createContext, type ComponentChildren } from "preact";
import { useContext } from "preact/hooks";
import type { Delta } from "loro-crdt";
import { components, cleanProps } from "./components";
import { instance } from "./library";
import { safeHref } from "./href";
import type { NodeJSON, TreeID } from "./doc";

// What the page needs from the editor around it; absent when published.
export type Host = {
  mountText(id: TreeID, el: HTMLElement): () => void;
  block?(node: NodeJSON, inner: ComponentChildren): ComponentChildren;
};
export const HostContext = createContext<Host | undefined>(undefined);

// Components made on the page, by name, and the ones being drawn now, so a
// component that contains itself is caught rather than drawn forever.
export const MadeContext = createContext<{ defs: Record<string, NodeJSON>; within: string[] }>({ defs: {}, within: [] });

export function defsOf(defs: NodeJSON[]): Record<string, NodeJSON> {
  const out: Record<string, NodeJSON> = {};
  for (const d of defs) if (typeof d.props.name === "string" && !components[d.props.name]) out[d.props.name] ??= d;
  return out;
}

export function PageView({ nodes }: { nodes: NodeJSON[] }) {
  return <>{nodes.map((n) => <NodeView key={n.id} node={n} />)}</>;
}

function NodeView({ node }: { node: NodeJSON }) {
  const host = useContext(HostContext);
  const inner = (
    <Guard id={node.id} type={node.type}>
      <Draw node={node} />
    </Guard>
  );
  return <>{host?.block ? host.block(node, inner) : inner}</>;
}

function Draw({ node }: { node: NodeJSON }) {
  const c = components[node.type];
  const made = useContext(MadeContext);
  const attrs = { "data-node": node.id };
  const children = node.children.map((n) => <NodeView key={n.id} node={n} />);
  const def = made.defs[node.type];
  if (!c && def) {
    if (made.within.includes(node.type)) return <Missing attrs={attrs} why={`“${node.type}” can't contain itself`} />;
    // An instance draws its definition's blocks as they are, with its own
    // overrides (any that break its schema are skipped); it is edited
    // through its settings, not in place.
    return (
      <div {...attrs} class="c-made" data-component={node.type}>
        <MadeContext.Provider value={{ defs: made.defs, within: [...made.within, node.type] }}>
          <HostContext.Provider value={undefined}>
            <PageView nodes={instance(def, node.props)} />
          </HostContext.Provider>
        </MadeContext.Provider>
      </div>
    );
  }
  if (!c) return <Missing attrs={attrs} why={`No component called “${node.type}” in this build`} props={node.props}>{children}</Missing>;
  return c.render({
    attrs,
    props: cleanProps(c, node.props),
    text: c.text ? <TextSlot id={node.id} delta={node.text ?? []} /> : undefined,
    children: c.children ? (children.length || c.optional ? children : <Empty id={node.id} />) : undefined,
  });
}

// What it says, as text: a tag the site draws (`<Serves story="…" />`)
// shows its props, which are edited in the panel and kept as written.
// meets: file/unknown-tags
function Missing({ attrs, why, props, children }: { attrs: Record<string, string>; why: string; props?: Record<string, unknown>; children?: ComponentChildren }) {
  const shown = Object.entries(props ?? {}).filter(([, v]) => typeof v === "string");
  return (
    <div {...attrs} class="missing" role="note">
      <span class="missing-why" contentEditable={false}>{why}</span>
      {shown.length > 0 && (
        <dl class="missing-props" contentEditable={false}>
          {shown.map(([k, v]) => [<dt key={`${k}:k`}>{k}</dt>, <dd key={`${k}:v`} data-prop={k}>{v as string}</dd>])}
        </dl>
      )}
      {children}
    </div>
  );
}

function Empty({ id }: { id: TreeID }) {
  const host = useContext(HostContext);
  return host ? <div class="empty" data-into={id} contentEditable={false}>Empty: drag a block here</div> : null;
}

// A component that throws draws as a placeholder; its siblings draw on.
class Guard extends PComponent<{ id: TreeID; type: string; children: ComponentChildren }, { error?: string }> {
  componentDidCatch(e: unknown) {
    this.setState({ error: String((e as Error)?.message ?? e) });
  }
  render() {
    if (this.state.error) return <Missing attrs={{ "data-node": this.props.id }} why={`“${this.props.type}” failed to draw: ${this.state.error}`} />;
    return <>{this.props.children}</>;
  }
}

// Text: ProseMirror when editing, plain marked-up text when published.
function TextSlot({ id, delta }: { id: TreeID; delta: Delta<string>[] }) {
  const host = useContext(HostContext);
  return host ? <Editable id={id} host={host} /> : <span class="tx">{delta.map(runOf)}</span>;
}

class Editable extends PComponent<{ id: TreeID; host: Host }> {
  private el?: HTMLElement;
  private stop?: () => void;
  componentDidMount() {
    this.stop = this.props.host.mountText(this.props.id, this.el!);
  }
  shouldComponentUpdate() {
    return false; // ProseMirror owns what's inside
  }
  componentWillUnmount() {
    this.stop?.();
  }
  render() {
    const ref = (el: HTMLSpanElement | null) => {
      this.el = el ?? undefined;
    };
    return <span class="tx" data-text={this.props.id} ref={ref} />;
  }
}

function runOf(op: Delta<string>, i: number) {
  if (!("insert" in op) || !op.insert) return null;
  const a = op.attributes ?? {};
  let out: ComponentChildren = op.insert;
  if (a.code) out = <code>{out}</code>;
  if (a.italic) out = <em>{out}</em>;
  if (a.bold) out = <strong>{out}</strong>;
  if (a.link) out = <a href={safeHref(a.link)}>{out}</a>;
  return <span key={i}>{out}</span>;
}
